// Import
import i18n from '@/i18n'
import Vue from 'vue'
import store from '@/store'
import router from '@/router'
import { Api } from '@/core/api.js'

import { FORMAT, TOOLS } from '@/constants'
import { buildRadarModuleState, canViewRadarTemplateEntry } from '@/store/utils/radarKpis.js'
import { buildHierarchicalOrderedItems, getBaseOrder, getItemsBySection as getItemsBySectionFromItems } from '@/store/utils/checklistItems'
import { postTaskWithRetry } from '@/utils/taskApiRetry'

import { db_action_plans } from '@/database'

const api = new Api()

// Triquiñuela para resetear el state
const getDefaultState = () => {
    return {
        items: {},
        templates: {},
        templatesLoaded: false, // needed by Radar
        type: 5,
        config: {
            date_format: 'YYYY-MM-DD',
            hour_format: 'HH:mm',
            datetime_format: 'YYYY-MM-DD HH:mm',
            has_score: false,
            has_state: false,
            has_timing: false,
            has_mandatory: false,
            is_autosave: false,
            can_create: false,
            item_search: false,
            has_calendar: false,
            temporal_view: false,
            close_when_completed: false,
            auth_users: {
                pin: 2,
                review: 2,
                task: 1
            },
            can_edit: {
                title: false,
                state: false,
                inPast: false,
                inTime: false,
                inFuture: false
            },
            item: {
                itemsTitle: [FORMAT.TITLE, FORMAT.SUBTITLE],
                itemsImage: [FORMAT.PHOTO, FORMAT.SIGNATURE],
                itemsInfo: [FORMAT.DOCUMENT, FORMAT.MESSAGE],
                itemsExcluded: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.DOCUMENT, FORMAT.MESSAGE, FORMAT.LINK, FORMAT.RESOURCE],
                itemsVisibles: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.MESSAGE],
                can_pospone: false,
                rate: false, // Esto es por si van a tener puntuaciones en funcion de lo que se responda
                can_review: false,
                has_periodicity: false,
                has_score: false
            }
        },
        headers: [
            { text: 'action_plans.headers.audit_id', key: 'auditDisplayId', sortable: true, visible: true, blocked: true },
            { text: 'action_plans.headers.created_at', key: 'createdDate', sortable: true, visible: true, blocked: true },
            { text: 'action_plans.headers.deadline_date', key: 'deadlineDate', sortable: true, visible: true, blocked: true },
            { text: 'action_plans.headers.status', key: 'status', sortable: true, visible: true, blocked: true },
            { text: 'action_plans.headers.progress', key: 'progressPercentage', sortable: true, visible: true, blocked: true },
            { text: 'action_plans.headers.non_conformity', key: 'nonConformity', sortable: true, visible: true, blocked: true }
        ],
        pageNumber: 1,
        itemsPerPage: 20,
        totalPendingActionPlans: null,
        actionPlanStatuses: [],
        isLoading: false,
        open_count: 0,
        hasApiError: false,
        radarKPIs: null
    }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
    getActionPlans: (state) => {
        return state.items
    },
    getById: (state, getters, rootState) => (id) => {
        return id ? state.items[id] : false
    },
    getConfig: (state, getters, rootState) => {
        return { ...state.config }
    },
    getTemplates: (state, getters, rootState) => {
        return state.templates
    },
    getTemplatesLoaded: (state) => {
        return state.templatesLoaded
    },
    getTemplateById: (state) => (id) => {
        if (id == null) {
            return null
        }

        const templates = state.templates || {}

        return templates[id] || null
    },
    getSubtasks: (state, getters, rootState) => (check, task) => {
        let tasks = check ? state.items[check].items : {}
        let subtask = []

        Object.keys(tasks).map(function (item_id, i) {
            var item = tasks[item_id]
            if (item.parent_id == task) {
                subtask.push(item.item_id)
            }
        })

        return subtask
    },
    getItemsBySection: (state) => (check, section) => {
        const items = check && state.items[check] ? state.items[check].items : null
        return getItemsBySectionFromItems(items, section)
    },
    getActionPlansHeaders: (state) => {
        return state.headers
    },
    getTotalPendingActionPlans: (state) => {
        return state.totalPendingActionPlans
    },
    getItemsPerPage: (state) => {
        return state.itemsPerPage
    },
    getPageNumber: (state) => {
        return state.pageNumber
    },
    getAccountActionPlansStatuses: (state) => {
        return state.actionPlanStatuses || []
    },
    getIsLoading: (state) => {
        return state.isLoading
    },
    getOpenCount: (state) => {
        return state.open_count
    },
    getHasApiError: (state) => {
        return state.hasApiError
    },
    getRadarModuleState: (state, getters, rootState, rootGetters) => (kpiKeys) => {
        const result = buildRadarModuleState(TOOLS.ACTION_PLANS, state.radarKPIs, kpiKeys, {}, rootGetters)

        return result
    },
    getOpenActionPlansByAuditTemplate: (state, getters, rootState, rootGetters) => {
        const openActionPlans = Array.isArray(state.radarKPIs) ? state.radarKPIs.find((kpi) => kpi && kpi.key === 'open_action_plans') : null
        const templates = openActionPlans && Array.isArray(openActionPlans.templates) ? openActionPlans.templates : []

        return templates.reduce((counts, template) => {
            if (!template || !template.audit_template_id) {
                return counts
            }

            if (!canViewRadarTemplateEntry(template, TOOLS.ACTION_PLANS, rootGetters)) {
                return counts
            }

            const auditTemplateId = String(template.audit_template_id)
            counts[auditTemplateId] = (counts[auditTemplateId] || 0) + (Number(template.value) || 0)

            return counts
        }, {})
    }
}

// actions
const actions = {
    loadActionPlansData: (context, params) => {
        const auditTemplateId = params.auditTemplateId

        const apiParams = {
            number_pages: context.state.pageNumber,
            number_items: context.state.itemsPerPage
        }

        context.commit('setIsLoading', true)

        return api
            .post(`checklist/resume/${auditTemplateId}/action_plans`, apiParams)
            .then((response) => {
                if (response.status) {
                    if (response.data && response.data.data) {
                        context.dispatch('format', { data: response.data.data })
                        context.dispatch('calcScore')
                        context.commit('setTotalPendingActionPlans', response.data.total_count)
                        if (response.data.open_count !== undefined) {
                            context.commit('setOpenCount', response.data.open_count)
                        }
                        context.commit('setHasApiError', false)
                    } else {
                        console.error('Error in loadActionPlansData():', response.msg)
                        context.commit('setHasApiError', true)
                    }
                } else {
                    console.error('Error in loadActionPlansData():', response.msg)
                    context.commit('setHasApiError', true)
                }
            })
            .catch((error) => {
                console.error('Error in loadActionPlans():', error)
                context.commit('setHasApiError', true)
            })
            .finally(() => {
                // Set loading to false when request completes (success or error)
                context.commit('setIsLoading', false)
            })
    },
    resetState({ commit }) {
        commit('resetState')
    },
    updateOpenActionPlansRadarKPIForAudit(context, params = {}) {
        const audit = params.audit
        const auditTemplateId = audit && audit.template_id
        const numericDelta = Number(params.delta) || 0
        const deltasByTemplate = {}

        if (!numericDelta || !audit || !audit.complete_date || auditTemplateId === null || auditTemplateId === undefined || auditTemplateId === false || auditTemplateId === '') {
            return []
        }

        Object.keys(audit.sections || {}).forEach((sectionId) => {
            const section = audit.sections[sectionId]

            Object.keys((section && section.items) || {}).forEach((itemId) => {
                const item = section.items[itemId]

                Object.keys((item && item.actionplans) || {}).forEach((actionPlanId) => {
                    const actionPlan = item.actionplans[actionPlanId]

                    if (!actionPlan) {
                        return
                    }

                    let statusType = null

                    if (actionPlan.status && typeof actionPlan.status === 'object' && actionPlan.status.type !== undefined && actionPlan.status.type !== null) {
                        statusType = parseInt(actionPlan.status.type)
                    } else {
                        const statusData = (context.state.actionPlanStatuses || []).find((status) => String(status.id) === String(actionPlan.status))
                        statusType = statusData && statusData.type !== undefined && statusData.type !== null ? parseInt(statusData.type) : null
                    }

                    if (![1, 2].includes(statusType)) {
                        return
                    }

                    const templateId = actionPlan.template_id || actionPlan.action_plan_template_id || actionPlan.actionplan_templateid || actionPlan.template || actionPlanId

                    if (templateId === null || templateId === undefined || templateId === false || templateId === '') {
                        return
                    }

                    const key = `${String(auditTemplateId)}::${String(templateId)}`

                    if (!deltasByTemplate[key]) {
                        deltasByTemplate[key] = {
                            audit_template_id: auditTemplateId,
                            template_id: templateId,
                            delta: 0
                        }
                    }

                    deltasByTemplate[key].delta += numericDelta
                })
            })
        })

        const deltas = Object.values(deltasByTemplate).filter((item) => item.delta !== 0)

        if (deltas.length > 0) {
            context.commit('applyOpenActionPlansRadarKPIDeltas', deltas)
        }

        return deltas
    },
    async updateOpenActionPlansRadarKPIForStatusChange(context, params = {}) {
        const auditState = context.rootState.audit || {}
        let auditActionPlan = null

        if (params.actionPlanId !== null && params.actionPlanId !== undefined && params.actionPlanId !== false && params.actionPlanId !== '') {
            const actionPlanIdString = String(params.actionPlanId)

            for (const auditId in auditState.items || {}) {
                const audit = auditState.items[auditId]

                Object.keys((audit && audit.sections) || {}).forEach((sectionId) => {
                    const section = audit.sections[sectionId]

                    Object.keys((section && section.items) || {}).forEach((itemId) => {
                        const item = section.items[itemId]

                        Object.keys((item && item.actionplans) || {}).forEach((actionPlanKey) => {
                            const actionPlan = item.actionplans[actionPlanKey]
                            const actionPlanIds = [actionPlanKey, actionPlan && actionPlan.id, actionPlan && actionPlan.id_checklist, actionPlan && actionPlan.checklist, actionPlan && actionPlan.checklist_id].filter((id) => id !== null && id !== undefined && id !== false && id !== '')

                            if (!auditActionPlan && actionPlanIds.some((id) => String(id) === actionPlanIdString)) {
                                auditActionPlan = {
                                    audit,
                                    actionPlan,
                                    actionPlanId: actionPlanKey
                                }
                            }
                        })
                    })
                })
            }
        }

        if (!auditActionPlan) {
            const actionPlan = context.state.items[params.actionPlanId]
            const auditReference = actionPlan && actionPlan.reference && typeof actionPlan.reference === 'object' ? Object.values(actionPlan.reference).find((reference) => reference && reference.checklist_id) : null
            const auditId = (auditReference && auditReference.checklist_id) || (actionPlan && (actionPlan.checklist_ref || actionPlan.audit || actionPlan.audit_id || actionPlan.id_template_audit))
            let audit = auditId && auditState.items ? auditState.items[auditId] : null

            if (!audit && auditId && context.rootGetters.getFastConnection) {
                const response = await api.get('checklist/id/' + auditId, {}).catch(() => ({ status: false }))
                const audits = response && response.status && response.data && typeof response.data === 'object' ? response.data : {}
                audit = audits[auditId] || Object.values(audits)[0] || null
            }

            if (audit) {
                auditActionPlan = {
                    audit,
                    actionPlan,
                    actionPlanId: params.actionPlanId
                }
            }
        }

        if (!auditActionPlan || !auditActionPlan.audit || !auditActionPlan.actionPlan || !auditActionPlan.audit.complete_date) {
            return []
        }

        const previousStatus = params.previousStatus !== undefined ? params.previousStatus : auditActionPlan.actionPlan.status
        const nextStatus = params.nextStatus
        let previousStatusType = null
        let nextStatusType = null

        if (previousStatus && typeof previousStatus === 'object' && previousStatus.type !== undefined && previousStatus.type !== null) {
            previousStatusType = parseInt(previousStatus.type)
        } else {
            const previousStatusData = (context.state.actionPlanStatuses || []).find((status) => String(status.id) === String(previousStatus))
            previousStatusType = previousStatusData && previousStatusData.type !== undefined && previousStatusData.type !== null ? parseInt(previousStatusData.type) : null
        }

        if (nextStatus && typeof nextStatus === 'object' && nextStatus.type !== undefined && nextStatus.type !== null) {
            nextStatusType = parseInt(nextStatus.type)
        } else {
            const nextStatusData = (context.state.actionPlanStatuses || []).find((status) => String(status.id) === String(nextStatus))
            nextStatusType = nextStatusData && nextStatusData.type !== undefined && nextStatusData.type !== null ? parseInt(nextStatusData.type) : null
        }

        const wasOpen = [1, 2].includes(previousStatusType)
        const isOpen = [1, 2].includes(nextStatusType)
        const delta = Number(isOpen) - Number(wasOpen)

        if (delta === 0) {
            return []
        }

        const auditTemplateId = auditActionPlan.audit.template_id
        const templateId = auditActionPlan.actionPlan.template_id || auditActionPlan.actionPlan.action_plan_template_id || auditActionPlan.actionPlan.actionplan_templateid || auditActionPlan.actionPlan.template || auditActionPlan.actionPlanId

        if (auditTemplateId === null || auditTemplateId === undefined || auditTemplateId === false || auditTemplateId === '' || templateId === null || templateId === undefined || templateId === false || templateId === '') {
            return []
        }

        const deltas = [
            {
                audit_template_id: auditTemplateId,
                template_id: templateId,
                delta
            }
        ]

        context.commit('applyOpenActionPlansRadarKPIDeltas', deltas)

        return deltas
    },
    async comment(context, params) {
        context.dispatch('calcScore', params.check)

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 1, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        const sended = {
            action: 'comment',
            checklist: params.check,
            task: params.answer,
            message: params.message,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            requireSync: false
        }

        if (typeof params.file !== 'undefined') sended.file = params.file

        // deja el item como estaba antes de intentar guardar la nota
        const revertComment = () => {
            const error = {
                check: params.check,
                answer: params.answer,
                requireSync: false
            }
            if (typeof params.file !== 'undefined') error.image = null
            else error.message = ''

            context.commit('setComment', error)
            return { status: false }
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            return revertComment()
        }

        const { response } = await postTaskWithRetry(api, 'checklist/task/comment', sended)

        if (!response.status) {
            return revertComment()
        }

        const returned = {
            check: params.check,
            answer: params.answer,
            message: response.data.message,
            requireSync: false
        }
        if (typeof response.data.image !== 'undefined') {
            returned.image = response.data.image
        }

        return context.commit('setComment', returned)
    },
    async validate(context, params) {
        context.commit('setValidation', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date,
            requireSync: false
        })
        await context.dispatch('calcScore', params.check)

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume)
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            context.commit('setReset', { check: params.check, answer: params.answer })
            return
        }

        return api
            .post('checklist/task/validate', paramsApi)
            .then(function (response) {
                if (!response.status) {
                    return context.commit('setReset', { check: params.check, answer: params.answer })
                }
                return true
            })
            .catch((error) => {
                logError(error)
                context.commit('setReset', { check: params.check, answer: params.answer })
            })
    },
    async unvalidate(context, params) {
        context.commit('setUnvalidation', {
            check: params.check,
            answer: params.answer,
            requireSync: false
        })
        await context.dispatch('calcScore', params.check)

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not issue
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume)
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            return
        }

        return api.post('checklist/task/unvalidate', paramsApi).catch((error) => {
            logError(error)
        })
    },
    complete(context, params) {
        context.dispatch('calcScore', params.checklist)

        let check = context.getters.getById(params.checklist)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        const paramsApi = {
            checklist: params.checklist,
            complete_date: params.complete_date,
            resume: JSON.stringify(resume)
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            return { status: false }
        }

        return api
            .post('checklist/complete', paramsApi)
            .then(function (response) {
                return response
            })
            .catch((error) => {
                logError(error)
                return { status: false }
            })
    },
    uncomplete(context, params) {
        const paramsApi = {
            checklist: params.checklist
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            return { status: false }
        }

        return api
            .post('checklist/uncomplete', paramsApi)
            .then(function (response) {
                return response
            })
            .catch((error) => {
                logError(error)
                return { status: false }
            })
    },
    loadById(context, params) {
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.get('checklist/id/' + params.id, {}).then(function (response) {
                return context.dispatch('format', { data: response.data, append: true }).then(() => {
                    return context.dispatch('calcScore', params.id)
                })
            })
        }
    },
    update(context, params) {
        const oData = { checklist: params.id }
        if (params.title) {
            oData.title = params.title
        }
        if (params.status) {
            oData.status = params.status
        }

        const previousStatus = params.status && context.state.items[params.id] ? context.state.items[params.id].status : undefined

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/update', oData).then(async function (response) {
                if (!response.status) {
                    return response
                }

                if (params.title) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'title',
                        value: oData.title
                    })
                }
                if (params.status) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'status',
                        value: oData.status
                    })
                    await context.dispatch('updateOpenActionPlansRadarKPIForStatusChange', {
                        actionPlanId: oData.checklist,
                        previousStatus,
                        nextStatus: oData.status
                    })
                    context.commit('audit/setActionPlanStatus', {
                        actionPlanId: oData.checklist,
                        status: oData.status
                    }, { root: true })
                }
                return response
            })
        } else {
            return Promise.reject(new Error('No connection available'))
        }
    },
    async answer(context, params) {
        let value = params.value
        let subtask = context.getters.getSubtasks(params.check, params.answer)

        // In case of image or signature type, JSON-parse the value
        if (state.config.item.itemsImage.includes(params.answer_type)) {
            value = JSON.stringify(value)
        }

        // answer
        context.commit('setAnswer', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date,
            value: params.value,
            requireSync: false
        })

        // calculate score
        await context.dispatch('calcScore', params.check)

        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // Actualizar estado basado en items respondidos
        await context.dispatch('updateStatusBasedOnItems', params.check)

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]
            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        const paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            value: value,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            type: params.answer_type
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            context.commit('setReset', { check: params.check, answer: params.answer })
            return context.dispatch('calcScore', params.check).then(function () {
                return { status: false }
            })
        }

        const { response } = await postTaskWithRetry(api, 'checklist/task/answer', paramsApi)

        if (!response.status) {
            context.commit('setReset', { check: params.check, answer: params.answer })
            return context.dispatch('calcScore', params.check).then(function () {
                if (response.msg && response.msg === 'Error: Api Error') {
                    return { status: false, msg: 'Error: Api Error' }
                }
                return { status: false }
            })
        }

        let answerValue = response.data.value
        if (state.config.item.itemsImage.includes(params.answer_type)) {
            answerValue = splitCommasBase64(answerValue)
            context.commit('setAnswer', {
                check: params.check,
                answer: params.answer,
                emp: params.emp,
                date: params.date,
                value: answerValue
            })
        }

        return response
    },
    async apply(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)
        let item = state.items[params.check].items[params.answer]
        let items = context.getters.getItemsBySection(params.check, params.answer)

        // apply
        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.apply == 0 ? params.date : false,
            apply: params.apply,
            requireSync: false
        })

        if (item.type == FORMAT.TITLE) {
            Object.keys(items).map(function (item_id, i) {
                let it = items[item_id]

                if (!it.mandatory) {
                    context.commit('setApply', { check: params.check, answer: it.item_id, date: params.apply == 0 ? params.date : false, apply: params.apply })
                }
            })
        }

        // calculate score
        await context.dispatch('calcScore', params.check)

        // reset child items
        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                let item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // Actualizar estado basado en items respondidos
        await context.dispatch('updateStatusBasedOnItems', params.check)

        // calculate score

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            apply: params.apply,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume)
        }

        const revertApply = () => {
            context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
        }

        if (!fnCheckConnection() || !context.rootGetters.getFastConnection) {
            revertApply()
            return
        }

        return api
            .post('checklist/task/apply', paramsApi)
            .then(function (response) {
                if (!response.status) {
                    revertApply()
                }
            })
            .catch((error) => {
                logError(error)
                revertApply()
            })
    },
    async applyrecurrent(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)

        // apply
        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.apply == 0 ? params.date : false,
            apply: params.apply
        })

        var item = state.items[params.check].items[params.answer]
        if (item.type == FORMAT.TITLE) {
            let items = context.getters.getItemsBySection(params.check, params.answer)

            Object.keys(items).map(function (item_id, i) {
                var it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', { check: params.check, answer: it.item_id, date: params.apply == 0 ? params.date : false, apply: params.apply })
                }
            })
        }

        // calculate score
        await context.dispatch('calcScore', params.check)

        // reset child items
        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // calculate score

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        return api
            .post('checklist/task/applyrecurrent', {
                checklist: params.check,
                task: params.answer,
                employee: params.emp,
                complete_date: params.date,
                apply: params.apply,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {
                if (!response.status) {
                    context.commit('setReset', { check: params.check, answer: params.answer })
                }
            })
    },
    async reset(context, params) {
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            let subtask = context.getters.getSubtasks(params.check, params.answer)

            context.commit('setReset', {
                check: params.check,
                answer: params.answer,
                requireSync: false
            })

            await context.dispatch('calcScore', params.check)

            if (subtask.length > 0) {
                Object.keys(subtask).map((item_id, i) => {
                    var item = subtask[item_id]
                    context.commit('setReset', { check: params.check, answer: item, requireSync: false })
                })
            }

            // Actualizar estado basado en items respondidos
            await context.dispatch('updateStatusBasedOnItems', params.check)

            let check = context.getters.getById(params.check)
            let total = check.total
            let answered = check.completed
            let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
            let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

            for (let id in check.items) {
                let item = check.items[id]

                if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                    if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                    if (!item.apply) resume.na++ // if item has not notes
                }
            }

            const paramsApi = {
                checklist: params.check,
                task: params.answer,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            }

            const { response } = await postTaskWithRetry(api, 'checklist/task/reset', paramsApi)

            return { status: !!response.status }
        } else {
            return { status: false }
        }
    },
    format({ state, context, commit, dispatch, rootState }, params = {}) {
        const append = typeof params.append !== 'undefined' ? params.append : false
        const aItems = {}

        if (typeof params.data !== 'undefined') {
            Object.keys(params.data).map(function (item_id, i) {
                var list = params.data[item_id]

                // Offline cases
                if (list.created_date && typeof list.created_date === 'string') {
                    list.created_date = moment(list.created_date)
                }

                if (list.business_date && typeof list.business_date === 'string') {
                    list.business_date = moment(list.business_date)
                }

                if (list.complete_date && typeof list.complete_date === 'string') {
                    list.complete_date = moment(list.complete_date)
                }

                // Timezone set
                list.business_date = list.business_date ? (moment.isMoment(list.business_date) ? list.business_date : moment(list.business_date * 1000)) : false
                list.created_date = list.created_date ? (moment.isMoment(list.created_date) ? list.created_date : moment(list.created_date * 1000)) : false
                list.complete_date = list.complete_date ? (moment.isMoment(list.complete_date) ? list.complete_date : moment(list.complete_date * 1000)) : false

                list.def = state.config

                // Loop for all items
                Object.keys(list.items).map(function (item_id, i) {
                    var item = list.items[item_id]

                    // Timezone set
                    item.complete_date = item.complete_date ? (moment.isMoment(item.complete_date) ? item.complete_date : moment(item.complete_date * 1000)) : false
                    if (item.review) {
                        item.review['complete_date'] = item.review.complete_date ? (moment.isMoment(item.review.complete_date) ? item.review.complete_date : moment(item.review.complete_date * 1000)) : false
                    }

                    // If type image - Split value to array
                    if (state.config.item.itemsImage.includes(item.type)) {
                        item.value = item.value && typeof item.value === 'string' ? splitCommasBase64(item.value) : item.value
                    }

                    item.complete_date = item.complete_date
                    item.available = false

                    list.items[item_id] = { ...item }
                    Vue.set(list.items, item_id, { ...item })
                })

                aItems[list.id] = list
            })
        }

        commit('setFormat', { items: aItems, append })
    },
    calcScore({ state, context, commit, dispatch, rootState }, id) {
        var aCategories = state.categories

        const checklists = state.items

        var alert_pending_count = {}
        var alert_pending_show = {}
        var alert_important_show = {}
        var c_alert_pending_count = {}
        var c_alert_pending_show = {}
        var c_alert_important_show = {}

        // SETTER
        Object.keys(checklists).map((checklistId, i) => {
            const checklist = checklists[checklistId]

            if (typeof id === 'undefined' || checklistId == id) {
                const now = moment().set({ s: 0, ms: 0 })
                const pastScore = checklist.score ? checklist.score : 0

                checklist.def = state.config
                checklist.uncompleted = 0
                checklist.completed = 0
                checklist.unanswered = 0
                checklist.total = 0
                checklist.score = -1
                checklist.grade = false
                checklist.to_review = 0
                checklist.planned_items = 0

                // Loop for all items
                Object.keys(checklist.items).map((itemId, i) => {
                    const item = checklist.items[itemId]

                    item.is_visible = true
                    item.alerts = false
                    // Subsubitem: item whose parent is itself a subitem (parent has parent_id)
                    const parent = item.parent_id ? checklist.items[item.parent_id] : null
                    item.is_subsubitem = !!(parent && parent.parent_id)

                    if (item.require_review && !item.review && item.apply && !item.planned_to_date) {
                        checklist.to_review++
                    }

                    // SUBITEMS
                    if (typeof item.conditions !== 'undefined' && item.conditions != null && item.conditions) {
                        const subitemConditionsData = item.conditions
                        const conditionsCombinator = typeof subitemConditionsData.operator !== 'undefined' ? subitemConditionsData.operator : 'AND'

                        const conditions = subitemConditionsData.values

                        let shouldDisplay = null
                        let parentItemOrder = item.parent_id && checklist.items[item.parent_id] ? parseFloat(checklist.items[item.parent_id].order) : -1

                        // condition key === subitem parent_id and parent_id === parent item item_id
                        for (const key in conditions) {
                            let show = false
                            item.is_visible = false

                            const condition = conditions[key]
                            const conditionOperator = condition && condition.operator ? condition.operator : '='
                            const conditionValue = condition && typeof condition.value !== 'undefined' ? condition.value : false

                            const parentConditionItem = checklist.items[key]
                            const parentItem = parentConditionItem || null
                            const parentItemValue = parentItem && typeof parentItem.value !== 'undefined' ? parentItem.value : null

                            if (parentConditionItem && (parentItemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(parentConditionItem.type)) && parentConditionItem.is_visible) {
                                if (conditionOperator == 'true' && conditionValue == 'true') {
                                    show = true
                                } else {
                                    // Get task type from parent
                                    switch (parentConditionItem.type) {
                                        case FORMAT.CHECK:
                                            show = compare(conditionValue, parentItemValue, conditionOperator)
                                            break
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            show = compare(parseInt(conditionValue) == 1 || (conditionValue == '' && conditionOperator == '!empty'), parentItemValue == true || parentItemValue == 'true' || (parentItemValue != null && conditionValue == '' && conditionOperator == '!empty'), conditionOperator)
                                            break
                                        case FORMAT.YESNONC:
                                            show = compare(parseInt(conditionValue) || (conditionValue == '' && conditionOperator == '!empty'), parentItemValue || (conditionValue == '' && conditionOperator == '!empty'), conditionOperator)
                                            break
                                        case FORMAT.MULTIPLE:
                                            const parentItemIsMultiselect = parentItem && parentItem.config && parentItem.config.hasOwnProperty('multiselect') && parentItem.config.multiselect === 1

                                            if (!parentItemIsMultiselect) {
                                                // SINGLE SELECT
                                                show = compare(conditionValue || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue, conditionOperator)
                                            } else {
                                                // MULTIPLE SELECT
                                                if (Array.isArray(parentItemValue) || (typeof parentItemValue === 'string' && parentItemValue.includes('|'))) {
                                                    const parentItemValues = Array.isArray(parentItemValue) ? parentItemValue : parentItemValue.split('|')
                                                    show = parentItemValues.some((value) => compare(conditionValue, value, conditionOperator))
                                                } else {
                                                    show = compare(conditionValue, parentItemValue, conditionOperator)
                                                }
                                            }
                                            break
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            show = compare(conditionValue, parentItemValue, conditionOperator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = parentItemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = parentItemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
                                            break
                                        default:
                                            break
                                    }
                                }
                            }
                            if (shouldDisplay == null) shouldDisplay = show
                            else {
                                switch (conditionsCombinator) {
                                    case 'AND':
                                        shouldDisplay = shouldDisplay && show
                                        break
                                    case 'OR':
                                        shouldDisplay = shouldDisplay || show
                                        break
                                }
                            }

                            if (parentItemOrder === -1 && parentConditionItem) {
                                parentItemOrder = parseFloat(parentConditionItem.order)
                            }
                        }

                        item.is_visible = shouldDisplay

                        const subitemOrder = getBaseOrder(item)
                        if (!Number.isNaN(parentItemOrder) && parentItemOrder !== -1 && !Number.isNaN(subitemOrder)) {
                            item.order = parentItemOrder + subitemOrder / 10000
                        } else {
                            item.order = subitemOrder
                        }
                    }

                    if (item.alerts_triggers) {
                        const alerts = item.alerts_triggers
                        const itemValue = item.value

                        let oAlert = {}
                        let message = ''
                        let preview = ''
                        let issues = []
                        let classes = []

                        // SHOW ALERTS
                        for (const i in alerts) {
                            const alert = alerts[i]
                            const operator = alert && typeof alert.operator !== 'undefined' ? alert.operator : '='
                            const alertValue = alert && typeof alert.value !== 'undefined' ? alert.value : false

                            const alertDate = alert.date ? alert.date : ''
                            let show = false

                            if ((itemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(item.type)) && item.is_visible && alert.type && alert.type != 'email') {
                                if (operator === 'true' && alertValue === 'true') {
                                    show = true
                                } else {
                                    switch (item.type) {
                                        case FORMAT.CHECK:
                                            show = compare(alertValue, itemValue, operator)
                                            break
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            show = compare(parseInt(alertValue) == 1 || (alertValue == '' && operator == '!empty'), itemValue == true || itemValue == 'true', operator)
                                            break
                                        case FORMAT.YESNONC:
                                            show = compare(parseInt(alertValue) || (alertValue == '' && operator == '!empty'), itemValue, operator)
                                            break
                                        case FORMAT.MULTIPLE:
                                            const itemIsMultiselect = item && item.config && item.config.hasOwnProperty('multiselect') && item.config.multiselect === 1

                                            // MULTIPLE SELECT
                                            if (itemIsMultiselect && (Array.isArray(itemValue) || (typeof itemValue === 'string' && itemValue.includes('|')))) {
                                                const itemValues = Array.isArray(itemValue) ? itemValue : itemValue.split('|')

                                                show = itemValues.some((value) => compare(alertValue || (alertValue === '' && operator === '!empty'), value, operator))
                                            } else {
                                                // SINGLE SELECT
                                                show = compare(alertValue || (alertValue === '' && operator === '!empty'), itemValue, operator)
                                            }

                                            break
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            show = compare(alertValue, itemValue, operator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = itemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = itemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        default:
                                            break
                                    }
                                }
                            }
                            if (show && (alert.msg || alert.message)) {
                                if (message == '') message = alert.msg != '' ? alert.msg : alert.message
                                else message = message + '<br/>' + (alert.msg != '' ? alert.msg : alert.message)

                                if (preview == '') preview = alert.msg != '' ? alert.msg : alert.message
                                else preview = preview + ', ' + (alert.msg != '' ? alert.msg : alert.message)
                            }

                            if (show && alert.tpl) {
                                issues.push(alert.tpl)
                            }
                            if (show && alert.class) {
                                if (alert.class == 'alert') {
                                    alert.class = 'warning'
                                }
                                classes.push(alert.class)
                            }

                            if (show && alert.template) {
                                if (message == '') message = i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
                                else message = message + '<br/>' + i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })

                                if (preview == '') preview = i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
                                else preview = preview + ', ' + i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
                            }
                        }

                        oAlert = {
                            preview: preview,
                            message: message,
                            issues: issues,
                            class: classes
                        }
                        if (oAlert.message.length > 0 || oAlert.issues.length > 0) {
                            item.alerts = oAlert
                            Vue.set(item, 'alerts', oAlert)
                        }
                    }

                    // Scoring
                    if (((!item.parent_id && checklist.def.item.itemsExcluded.indexOf(item.type) == -1) || (item.parent_id && item.is_visible)) && !item.planned_to_date && item.apply) {
                        switch (item.type) {
                            case FORMAT.YESNO: // SI/NO
                            case FORMAT.YESNONC: // SI/NO/NSNC
                            case FORMAT.CHECK: // CHECKS
                            case FORMAT.TEXT: // TEXTO
                            case FORMAT.PHOTO: // FOTO
                            case FORMAT.NUMBER: // NUMBER
                            case FORMAT.DATE: // DATE
                            case FORMAT.TEXTAREA: // TEXTAREA
                            case FORMAT.MULTIPLE: // MULTIPLE SELECT
                            case FORMAT.TIME: // TIME
                            case FORMAT.DATETIME: // DATETIME
                            case FORMAT.SIGNATURE: // SIGNATURE
                            case FORMAT.TEMPERATURE: // TEMPERATURE
                            case FORMAT.EMAIL: // EMAIL
                            case FORMAT.DOCUMENT: // DOCUMENT
                                if (item.complete_date == false && item.apply) checklist.unanswered++
                                else {
                                    // Required to be reviewed
                                    if (item.require_review) {
                                        checklist.completed += item.review ? 1 : 0.5
                                    } else checklist.completed += 1 // Not required to be reviewed
                                }

                                break

                            default:
                                break
                        }
                        if (![FORMAT.LINK, FORMAT.RESOURCE].includes(item.type)) {
                            checklist.total++
                        }
                    }

                    if (item.planned_to_date) {
                        checklist.planned_items++
                    }
                })
                let divisor = parseInt(checklist.total + checklist.planned_items) == 0 ? 1 : parseInt(checklist.total + checklist.planned_items)
                checklist.score = Math.round((parseFloat(checklist.completed + checklist.planned_items) * 100) / divisor)

                checklist.need_justification = false

                let limit_start = moment(null)
                let limit_end = moment(null)
                let schedule_start = moment(null)
                let schedule_end = moment(null)

                if (!now.isBetween(limit_start, limit_end) && parseInt(checklist.score) < 100 && now.isAfter(limit_end) && checklist.completed != checklist.total) {
                    checklist.need_justification = true
                    alert_pending_count[checklist.timeframe_id]++
                    if (checklist.category_id && aCategories[checklist.category_id]) {
                        c_alert_pending_count[checklist.category_id]++
                    }
                    if (aCategories['all']) {
                        c_alert_pending_count['all']++
                    }

                    if (!checklist.justify_id) {
                        alert_pending_show[checklist.timeframe_id] = true
                        if (checklist.category_id && aCategories[checklist.category_id]) {
                            c_alert_pending_show[checklist.category_id]++
                        }
                        if (aCategories['all']) {
                            c_alert_pending_show['all']++
                        }
                    }
                }

                if (now.isBetween(limit_start, limit_end) && !now.isBetween(schedule_start, schedule_end) && now.isAfter(schedule_end) && checklist.important && parseInt(checklist.score) < 100) {
                    alert_important_show[checklist.timeframe_id] = parseInt(checklist.score) < 100
                    if (checklist.category_id && aCategories[checklist.category_id]) {
                        c_alert_important_show[checklist.category_id]++
                    }
                    if (aCategories['all']) {
                        c_alert_important_show['all']++
                    }

                    checklist.important = parseInt(checklist.score) < 100
                }

                checklists[checklist.id] = { ...checklist }
            }
        })

        // ORDER
        Object.keys(checklists).map(function (item_id, i) {
            let list = Object.assign({}, checklists[item_id])
            list.items = buildHierarchicalOrderedItems(list.items)
            checklists[item_id] = list
        })

        // Inicializar _previousAnsweredCount después de calcular is_visible
        // Solo inicializar si no existe para evitar sobrescribir valores existentes
        Object.keys(checklists).forEach((checklistId) => {
            const checklist = checklists[checklistId]
            if (checklist && checklist.items && checklist._previousAnsweredCount === undefined) {
                let answeredItemsCount = 0
                const itemsExcluded = checklist.def && checklist.def.item ? checklist.def.item.itemsExcluded : []

                Object.values(checklist.items).forEach((item) => {
                    // Usar is_visible que ya fue calculado por calcScore
                    if (item.is_visible && !itemsExcluded.includes(item.type)) {
                        const hasCompleteDate = item.complete_date !== null && item.complete_date !== undefined && item.complete_date !== false && (typeof item.complete_date === 'number' || typeof item.complete_date === 'object' || (typeof item.complete_date === 'string' && item.complete_date.length > 0))

                        const hasValidValue = item.value !== null && item.value !== undefined && item.value !== '' && item.value !== 'null' && item.value !== '""' && item.value !== '\\"\\"' && !(typeof item.value === 'string' && item.value.trim() === '')

                        if (hasCompleteDate || (item.apply && hasValidValue)) {
                            answeredItemsCount++
                        }
                    }
                })

                checklist._previousAnsweredCount = answeredItemsCount
            }
        })
    },
    /**
     * Actualiza el estado del plan de acción basado en los items respondidos
     * - Si se responde el primer item (pasa de 0 a 1), cambia a "En progreso" (type: 2, default: true)
     * - Si se borran todas las respuestas (pasa de >0 a 0) y el estado no es "Abierto", cambia a "Abierto" (type: 1)
     */
    async updateStatusBasedOnItems(context, checklistId) {
        const checklist = context.getters.getById(checklistId)
        if (!checklist || !checklist.items) {
            return
        }

        // Contar items respondidos actuales
        // Un item está respondido si tiene complete_date válido O (apply = true y value válido)
        let answeredItemsCount = 0
        const itemsExcluded = checklist.def && checklist.def.item ? checklist.def.item.itemsExcluded : []

        Object.values(checklist.items).forEach((item) => {
            if (item.is_visible && !itemsExcluded.includes(item.type)) {
                // Verificar si tiene complete_date válido
                const hasCompleteDate = item.complete_date !== null && item.complete_date !== undefined && item.complete_date !== false && (typeof item.complete_date === 'number' || typeof item.complete_date === 'object' || (typeof item.complete_date === 'string' && item.complete_date.length > 0))

                // Verificar si tiene value válido
                const hasValidValue = item.value !== null && item.value !== undefined && item.value !== '' && item.value !== 'null' && item.value !== '""' && item.value !== '\\"\\"' && !(typeof item.value === 'string' && item.value.trim() === '')

                // Un item está respondido si tiene complete_date válido O (apply = true y value válido)
                if (hasCompleteDate || (item.apply && hasValidValue)) {
                    answeredItemsCount++
                }
            }
        })

        // Obtener los estados disponibles
        const actionPlanStatuses = context.state.actionPlanStatuses || []
        if (actionPlanStatuses.length === 0) {
            return
        }

        // Obtener el estado actual
        const currentStatus = actionPlanStatuses.find((status) => {
            return String(status.id) === String(checklist.status) || parseInt(status.id) === parseInt(checklist.status)
        })
        const currentStatusType = currentStatus ? parseInt(currentStatus.type) : null

        // Obtener el conteo anterior (guardado en el checklist si existe)
        const previousAnsweredCount = checklist._previousAnsweredCount !== undefined ? checklist._previousAnsweredCount : 0

        // Guardar el conteo actual para la próxima vez
        context.commit('setChecklistProp', {
            check: checklistId,
            prop: '_previousAnsweredCount',
            value: answeredItemsCount
        })

        // Si se respondió el primer item (pasa de 0 a 1)
        if (previousAnsweredCount === 0 && answeredItemsCount === 1) {
            // Buscar el estado "En progreso" con type: 2 y default: true
            const inProgressStatus = actionPlanStatuses.find((status) => {
                const statusType = parseInt(status.type)
                return statusType === 2 && status.default === true
            })

            // Solo cambiar a "En progreso" si el estado actual es "Abierto" (type: 1)
            // Esto previene sobrescribir estados manualmente establecidos como "Cerrado"
            if (inProgressStatus && currentStatusType === 1) {
                const previousStatus = checklist.status
                // Actualizar el estado a "En progreso" sin hacer llamada a la API
                context.commit('setChecklistProp', {
                    check: checklistId,
                    prop: 'status',
                    value: inProgressStatus.id
                })
                await context.dispatch('updateOpenActionPlansRadarKPIForStatusChange', {
                    actionPlanId: checklistId,
                    previousStatus,
                    nextStatus: inProgressStatus.id
                })
                context.commit('audit/setActionPlanStatus', {
                    actionPlanId: checklistId,
                    status: inProgressStatus.id
                }, { root: true })
            }
        } else if (answeredItemsCount === 0) {
            // Si todos los items están vacíos (answeredItemsCount === 0)
            // Buscar el estado "Abierto" con type: 1 y default: true
            const openStatus = actionPlanStatuses.find((status) => {
                const statusType = parseInt(status.type)
                return statusType === 1 && status.default === true
            })

            // Cambiar a "Abierto" si el estado actual no es "Abierto"
            // Esto cubre tanto cuando se borran todas las respuestas como cuando todos están vacíos desde el inicio
            if (openStatus && currentStatusType !== 1) {
                const previousStatus = checklist.status
                // Actualizar el estado a "Abierto" sin hacer llamada a la API
                context.commit('setChecklistProp', {
                    check: checklistId,
                    prop: 'status',
                    value: openStatus.id
                })
                await context.dispatch('updateOpenActionPlansRadarKPIForStatusChange', {
                    actionPlanId: checklistId,
                    previousStatus,
                    nextStatus: openStatus.id
                })
                context.commit('audit/setActionPlanStatus', {
                    actionPlanId: checklistId,
                    status: openStatus.id
                }, { root: true })
            }
        }
    }
}

// mutations
const mutations = {
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setTemplates(state, payload) {
        const templates = {}

        if (payload.data !== undefined) {
            Object.keys(payload.data).map((templateId, i) => {
                const tpl = { ...payload.data[templateId] }

                tpl.last_update = tpl.last_update ? (moment.isMoment(tpl.last_update) ? tpl.last_update : moment(tpl.last_update * 1000)) : false

                templates[tpl.id] = tpl
            })
        }

        Vue.set(state, 'templates', { ...templates })
    },
    setTemplatesLoaded(state, payload) {
        state.templatesLoaded = payload
    },
    setAnswerProp(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var prop = payload.prop
        var value = payload.value
        var section = payload.getters['getSectionFromItem'](check, answer)

        state.items[check].sections[section].items[answer][prop] = value

        Vue.set(state, 'items', { ...state.items })
    },
    setComment(state, payload) {
        let check = payload.check
        let answer = payload.answer
        let aux = state.items[check].items[answer]

        aux.message = payload.message
        aux.image = payload.image ? payload.image : false
        aux.requireSync = payload.requireSync

        Vue.set(state.items[check].items, answer, { ...aux })
        Vue.set(state, 'items', { ...state.items })
    },
    setAnswer(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var value = payload.value
        var date = payload.date

        state.items[check].items[answer].value = value
        state.items[check].items[answer].requireSync = payload.requireSync

        if (date) {
            var item = state.items[check].items[answer]
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) state.items[check].items[answer].employee = emp

        Vue.set(state, 'items', { ...state.items })
    },
    setApply(state, payload) {
        const check = payload.check
        const answer = payload.answer
        const emp = payload.emp
        const apply = payload.apply
        const date = payload.date

        state.items[check].items[answer].apply = apply
        state.items[check].items[answer].requireSync = payload.requireSync

        if (date) {
            state.items[check].items[answer].complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : state.items[check].items[answer].complete_date
            state.items[check].items[answer].value = null
        } else {
            state.items[check].items[answer].complete_date = false
        }

        if (emp) {
            state.items[check].items[answer].employee = emp
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setReset(state, payload) {
        var check = payload.check
        var answer = payload.answer
        const sync = payload.requireSync ? payload.requireSync : false

        state.items[check].items[answer].complete_date = false
        state.items[check].items[answer].employee = false
        state.items[check].items[answer].review = false
        state.items[check].items[answer].value = null
        state.items[check].items[answer].requireSync = sync

        Vue.set(state, 'items', { ...state.items })
    },
    setValidation(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var date = payload.date

        state.items[check].items[answer].review = {}
        state.items[check].items[answer].requireSync = payload.requireSync

        if (date) {
            var item = state.items[check].items[answer].review
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) {
            var employee = store.getters['employee/getEmployee'](emp)
            state.items[check].items[answer].review.employee = employee
        }
        state.items[check].items[answer].review.value = 1

        Vue.set(state, 'items', { ...state.items })
    },
    setPostposed(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var date = payload.date
        var reason_id = payload.reason_id
        var reason_msg = payload.reason_msg
        let employee = payload.employee

        state.items[check].items[answer].planned_reason_id = reason_id
        state.items[check].items[answer].planned_reason_msg = reason_msg
        state.items[check].items[answer].planned_to_date = date
        state.items[check].items[answer].employee = employee

        Vue.set(state, 'items', { ...state.items })
    },
    setUnvalidation(state, payload) {
        var check = payload.check
        var answer = payload.answer

        state.items[check].items[answer].review = false
        state.items[check].items[answer].requireSync = payload.requireSync

        Vue.set(state, 'items', { ...state.items })
    },
    // Common but may change in other tools
    setFormat(state, payload) {
        if (payload.append) {
            // Agregar nuevos items a los existentes
            state.items = { ...state.items, ...payload.items }
            Vue.set(state, 'items', { ...state.items, ...payload.items })
        } else {
            // Reemplazar completamente
            state.items = payload.items
            Vue.set(state, 'items', { ...payload.items })
        }
    },
    setScore(state, payload) {
        state.items = payload.items

        Vue.set(state, 'items', { ...payload.items })
    },
    setJustified(state, payload) {
        var check = payload.check
        var reason_id = payload.reason_id
        var reason_msg = payload.reason_msg
        var justify_user_id = payload.justify_user_id
        var justify_date = payload.justify_date

        state.items[check].justify_id = reason_id
        state.items[check].justify_msg = reason_msg
        state.items[check].justify_user_id = justify_user_id
        state.items[check].justify_date = justify_date

        Vue.set(state, 'items', { ...state.items })
    },
    setItem(state, payload) {
        var item = payload
        state.items[item.id] = { ...item }
        Vue.set(state, 'items', { ...state.items })
    },
    setTotalPendingActionPlans: (state, totalPendingActionPlans) => {
        state.totalPendingActionPlans = totalPendingActionPlans
    },
    setPageNumber: (state, pageNumber) => {
        state.pageNumber = pageNumber
    },
    setItemsPerPage: (state, itemsPerPage) => {
        state.itemsPerPage = itemsPerPage
    },
    setActionPlanStatuses: (state, actionPlanStatuses) => {
        state.actionPlanStatuses = actionPlanStatuses || []
    },
    setIsLoading: (state, isLoading) => {
        state.isLoading = isLoading
    },
    setOpenCount: (state, openCount) => {
        state.open_count = openCount || 0
    },
    setChecklistProp(state, payload) {
        const check = payload.check
        const prop = payload.prop
        const value = payload.value

        if (state.items[check]) {
            state.items[check][prop] = value
            Vue.set(state, 'items', { ...state.items })
        }
    },
    setHasApiError: (state, hasApiError) => {
        state.hasApiError = hasApiError
    },
    setRadarKPIs(state, radarKPIs) {
        state.radarKPIs = radarKPIs
    },
    applyOpenActionPlansRadarKPIDeltas(state, payload) {
        const deltas = Array.isArray(payload) ? payload : [payload]

        if (!deltas.length) {
            return
        }

        const baseRadarKPIs = Array.isArray(state.radarKPIs) ? state.radarKPIs : []
        let found = false
        const updatedRadarKPIs = baseRadarKPIs.map((rawKpi) => {
            if (!rawKpi || rawKpi.key !== 'open_action_plans') {
                return rawKpi
            }

            found = true
            const templatesByKey = {}
            const currentTemplates = Array.isArray(rawKpi.templates) ? rawKpi.templates : []

            currentTemplates.forEach((template) => {
                if (!template || template.audit_template_id === null || template.audit_template_id === undefined || template.audit_template_id === false || template.audit_template_id === '' || template.template_id === null || template.template_id === undefined || template.template_id === false || template.template_id === '') {
                    return
                }

                const key = `${String(template.audit_template_id)}::${String(template.template_id)}`
                const current = templatesByKey[key]
                const value = Number(template.value)
                const currentValue = current ? Number(current.value) : 0

                templatesByKey[key] = {
                    ...(current || template),
                    audit_template_id: current ? current.audit_template_id : template.audit_template_id,
                    template_id: current ? current.template_id : template.template_id,
                    value: (Number.isNaN(currentValue) ? 0 : currentValue) + (Number.isNaN(value) ? 0 : value)
                }
            })

            deltas.forEach((item) => {
                if (!item || !item.delta || item.audit_template_id === null || item.audit_template_id === undefined || item.audit_template_id === false || item.audit_template_id === '' || item.template_id === null || item.template_id === undefined || item.template_id === false || item.template_id === '') {
                    return
                }

                const key = `${String(item.audit_template_id)}::${String(item.template_id)}`
                const current = templatesByKey[key]
                const currentValue = current ? Number(current.value) : 0

                templatesByKey[key] = {
                    ...(current || {}),
                    audit_template_id: current ? current.audit_template_id : item.audit_template_id,
                    template_id: current ? current.template_id : item.template_id,
                    value: (Number.isNaN(currentValue) ? 0 : currentValue) + Number(item.delta)
                }
            })

            return {
                ...rawKpi,
                templates: Object.values(templatesByKey).map((template) => {
                    const value = Number(template.value)

                    return {
                        ...template,
                        value: Number.isNaN(value) ? 0 : value
                    }
                }).filter((template) => template.value > 0)
            }
        })

        if (!found) {
            const templatesByKey = {}

            deltas.forEach((item) => {
                if (!item || !item.delta || item.audit_template_id === null || item.audit_template_id === undefined || item.audit_template_id === false || item.audit_template_id === '' || item.template_id === null || item.template_id === undefined || item.template_id === false || item.template_id === '') {
                    return
                }

                const key = `${String(item.audit_template_id)}::${String(item.template_id)}`
                const current = templatesByKey[key]
                const currentValue = current ? Number(current.value) : 0

                templatesByKey[key] = {
                    ...(current || {}),
                    audit_template_id: current ? current.audit_template_id : item.audit_template_id,
                    template_id: current ? current.template_id : item.template_id,
                    value: (Number.isNaN(currentValue) ? 0 : currentValue) + Number(item.delta)
                }
            })
            const templates = Object.values(templatesByKey).map((template) => {
                const value = Number(template.value)

                return {
                    ...template,
                    value: Number.isNaN(value) ? 0 : value
                }
            }).filter((template) => template.value > 0)

            if (!templates.length) {
                return
            }

            updatedRadarKPIs.push({
                key: 'open_action_plans',
                templates,
                severity: 'warning'
            })
        }

        Vue.set(state, 'radarKPIs', updatedRadarKPIs)
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
